import { createElement, Fragment, type ReactNode } from 'react'
import { Markdown } from '@bloo-team/ui'

/**
 * A detection's knowledge base. signals/get_kb returns HTML for most
 * detections (`<h2>Description</h2><p>…`) and markdown for some; HTML is
 * parsed (DOMParser, inert — nothing runs, nothing loads) and an allow-list
 * of tags is rebuilt as React elements, never innerHTML. Everything else is
 * unwrapped to its text; script-like elements are dropped with their
 * content. Attributes are dropped too, except an http(s) link's href.
 * Markdown (or plain text) goes to bloo-ui's Markdown. Both render inside
 * `.bui-md`, so the two read the same.
 */

/** Looks like HTML: at least one block or inline tag the allow-list knows. */
export const KB_HTML = /<\/?(?:html|body|div|p|h[1-6]|ul|ol|li|br|hr|strong|b|em|i|code|pre|table|thead|tbody|tr|th|td|blockquote|a|span)\b[^>]*>/i

/** Kept as themselves. Headings shift down two levels: the drawer's own title is the h2. */
const KEEP: Record<string, string> = {
  h1: 'h3',
  h2: 'h3',
  h3: 'h4',
  h4: 'h5',
  h5: 'h6',
  h6: 'h6',
  p: 'p',
  ul: 'ul',
  ol: 'ol',
  li: 'li',
  strong: 'strong',
  b: 'strong',
  em: 'em',
  i: 'em',
  code: 'code',
  pre: 'pre',
  blockquote: 'blockquote',
  table: 'table',
  thead: 'thead',
  tbody: 'tbody',
  tr: 'tr',
  th: 'th',
  td: 'td',
}
const VOID: Record<string, string> = { br: 'br', hr: 'hr' }
/** Dropped with everything inside them. */
const DROP = new Set(['script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template', 'head', 'title', 'meta', 'link', 'svg', 'math', 'img', 'video', 'audio', 'form', 'input', 'button', 'select', 'textarea'])
const SAFE_HREF = /^https?:\/\//i

function children(node: Node, key: string): ReactNode[] {
  return [...node.childNodes].map((c, i) => convert(c, `${key}-${i}`))
}

function convert(node: Node, key: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent
  if (node.nodeType !== Node.ELEMENT_NODE) return null
  const tag = (node as Element).tagName.toLowerCase()
  if (DROP.has(tag)) return null
  if (VOID[tag]) return createElement(VOID[tag], { key })
  if (tag === 'a') {
    const href = (node as Element).getAttribute('href')?.trim() ?? ''
    const kids = children(node, key)
    return SAFE_HREF.test(href) ? (
      <a key={key} href={href} target="_blank" rel="noopener noreferrer">
        {kids}
      </a>
    ) : (
      <Fragment key={key}>{kids}</Fragment>
    )
  }
  const kept = KEEP[tag]
  if (kept) {
    // A table scrolls on its own, like Markdown's.
    const el = createElement(kept, { key }, ...children(node, key))
    return tag === 'table' ? (
      <div key={key} className="bui-md-table">
        {el}
      </div>
    ) : (
      el
    )
  }
  // html, body, div, span, font, … : the wrapper goes, its content stays.
  return <Fragment key={key}>{children(node, key)}</Fragment>
}

/**
 * Whitespace-only text between blocks (the newlines of pretty-printed
 * HTML) would render as stray spaces inside the grid; trim them out.
 */
function blocks(body: HTMLElement): ReactNode[] {
  return [...body.childNodes].filter((n) => n.nodeType !== Node.TEXT_NODE || n.textContent?.trim()).map((c, i) => convert(c, `kb-${i}`))
}

export function KbContent({ text }: { text: string }) {
  if (!KB_HTML.test(text)) return <Markdown text={text} links />
  const doc = new DOMParser().parseFromString(text, 'text/html')
  return <div className="bui-md">{blocks(doc.body)}</div>
}
